// src/app/products/page.jsx
import React, { useState } from "react";
import { products } from "../../lib/data";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { ShoppingCart, Heart, Search, Check } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { AnalyticsService } from "../../services/analyticsService";
import { useToast } from "../../hooks/toast";
import { LogoSimple } from "../../components/Logo";

const categories = ["All", "Flowers", "Soaps", "Ceramic & Pottery", "Candles", "Special Edition"];

const subCategories = {
  Flowers: ["All", "Real Flowers", "Synthetic Flowers", "Dried Flowers", "Small Plants", "Wood Sticks"],
  Soaps: ["All", "Powdery", "Gourmet", "Oriental", "Woody", "Elegant", "Sweet"],
  "Ceramic & Pottery": ["All", "Vases", "Coasters"],
  Candles: ["All", "Jar Candle", "Shaped Candle"],
};

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeSubCategory, setActiveSubCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState(new Set());
  const [addedToCart, setAddedToCart] = useState(new Set());
  const { addToCart } = useCart();
  const { toast } = useToast();

  const filteredProducts = products.filter((product) => {
    const matchesCategory = activeCategory === "All" || product.category === activeCategory;
    const matchesSubCategory = activeSubCategory === "All" || product.subCategory === activeSubCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (product.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSubCategory && matchesSearch;
  });

  const toggleFavorite = (productId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(productId)) {
      newFavorites.delete(productId);
    } else {
      newFavorites.add(productId);
    }
    setFavorites(newFavorites);
  };

  const handleAddToCart = async (product) => {
    await addToCart(product, 1);
    setAddedToCart(new Set(addedToCart).add(product.id));

    // Track analytics
    AnalyticsService.trackAddToCart(product, 1);
    AnalyticsService.logCustomEvent('add_to_cart', { productId: product.id, productName: product.name });

    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });

    // Reset button state after 2 seconds
    setTimeout(() => {
      setAddedToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }, 2000);
  };

  // Track search analytics
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length > 2) {
      AnalyticsService.trackSearch(query);
      AnalyticsService.logCustomEvent('search', { query });
    }
  };

  const currentSubCategories = activeCategory !== "All" && subCategories[activeCategory]
    ? subCategories[activeCategory]
    : null;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Our Collections
          </h1>
          <p className="text-xl text-muted-foreground">
            Discover artisanal goods crafted with passion and care
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8 max-w-xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              type="text"
              placeholder="Search products..."
              className="pl-10 py-6 text-lg backdrop-blur-sm bg-background/70 border-2 border-border"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "outline"}
                className={`rounded-full px-6 transition-all duration-300 ${
                  activeCategory === category
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                    : "hover:border-primary/60"
                }`}
                onClick={() => {
                  setActiveCategory(category);
                  setActiveSubCategory("All");
                }}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Subcategory Filters */}
        {currentSubCategories && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 justify-center">
              {currentSubCategories.map((subCat) => (
                <Button
                  key={subCat}
                  variant={activeSubCategory === subCat ? "secondary" : "ghost"}
                  size="sm"
                  className={`rounded-full ${
                    activeSubCategory === subCat
                      ? "bg-secondary text-secondary-foreground"
                      : ""
                  }`}
                  onClick={() => setActiveSubCategory(subCat)}
                >
                  {subCat}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-6 text-center">
          <p className="text-muted-foreground">
            Showing <span className="font-bold text-primary">{filteredProducts.length}</span> products
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card
              key={product.id}
              className="group overflow-hidden hover:shadow-2xl transition-all duration-300 backdrop-blur-sm bg-card/70 border-2 border-transparent hover:border-border"
            >
              <CardHeader className="p-0 relative">
                <div className="aspect-square bg-gradient-to-br from-muted/60 via-background to-muted/40 overflow-hidden relative">
                  {/* Placeholder image with swirl logo - would be replaced with actual product images */}
                  <div className="w-full h-full flex items-center justify-center">
                    <LogoSimple className="h-20 w-auto opacity-50" />
                  </div>
                  {/* Favorite Button */}
                  <button
                    onClick={() => toggleFavorite(product.id)}
                    className="absolute top-3 right-3 p-2 rounded-full backdrop-blur-md bg-background/70 hover:scale-110 transition-transform duration-200"
                  >
                    <Heart
                      className={`h-5 w-5 ${
                        favorites.has(product.id)
                          ? "fill-primary text-primary"
                          : "text-muted-foreground"
                      }`}
                    />
                  </button>
                  {product.subCategory && product.category !== "Special Edition" && (
                    <Badge className="absolute bottom-3 left-3 bg-background/90 text-foreground">
                      {product.subCategory}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <CardTitle className="text-xl mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent group-hover:bg-clip-text transition-all duration-300">
                  {product.name}
                </CardTitle>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-primary">
                    ${product.price.toFixed(2)}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAddToCart(product)}
                    disabled={addedToCart.has(product.id)}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30 disabled:opacity-75"
                  >
                    {addedToCart.has(product.id) ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Added!
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-2xl text-muted-foreground">No products found</p>
            <p className="text-muted-foreground mt-2">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>
    </div>
  );
}
